import { ZodTypeAny } from 'zod';

/* eslint-disable @typescript-eslint/no-explicit-any */
export type ArrayItem<T> = T extends Array<infer I> ? I : unknown;
export type ArrayItem2<T> = T extends Array<infer I> ? I : T;
export type TypedOmit<T, K extends keyof T> = Omit<T, K>;
export type F0<Res = void> = () => Res;
export type F1<T1, Res = void> = (p1: T1) => Res;
export type F2<T1, T2, Res = void> = (p1: T1, p2: T2) => Res;
export type F3<T1, T2, T3, Res = void> = (p1: T1, p2: T2, p3: T3) => Res;
export type AnyFn = (...args: any) => any;
export type EmptyObject = Record<PropertyKey, unknown>;

export type TMap<TK extends KeyType, TV> = Record<TK, TV>;
export type SMap<T> = TMap<string, T>;
export type Casted<T, S> = TMap<keyof T, S>;
export type Comparable = { id: string };
export type AnyObject = SMap<any>;

declare const __brand: unique symbol;
export type Branded<T, TBrand> = T & { [__brand]: TBrand };

export type Typed<T extends string = string> = { type: T };
export type TypedValue<T extends string = string, V = any> = Typed<T> & {
  value: V;
};
export type FlattenTypedValue<T extends string = string, V = any> = Typed<T> &
  V;

export type AsyncError<T = string> = TypedValue<'fetch-error', T>;
export type AsyncFetched<T = string> = TypedValue<'fetched', T>;
export type AsyncNotFetched = Typed<'not-fetched'>;
export type AsyncFetching = Typed<'fetching'>;
export type Async<TValue = unknown, TError = string> =
  | AsyncNotFetched
  | AsyncFetching
  | AsyncFetched<TValue>
  | AsyncError<TError>;

export type AsyncValue<T> = T extends AsyncFetched<infer V> ? V : never;

export const toAsyncFetched = <T>(v: T): AsyncFetched<T> => ({
  type: 'fetched',
  value: v,
});

export const toAsyncError = <T = string>(v: T): AsyncError<T> => ({
  type: 'fetch-error',
  value: v,
});

export type AsyncType = Async['type'];

export type KeyType = string | number | symbol;
export type Action<T extends KeyType> = { type: T };
export type ActionWithPayload<T extends KeyType, P> = Action<T> & {
  payload: P;
};

export type State<T extends KeyType> = { type: T };
export type StateWithPayload<T extends KeyType, P> = State<T> & { value: P };

export type Parametrize<T> = T extends null ? [] : [T];

export type Dispatchers<TActionsMap extends SMap<any>> = {
  lazyDispatch: <T extends keyof TActionsMap>(
    type: T,
  ) => (...args: Parametrize<TActionsMap[T]>) => void;
  dispatch: <T extends keyof TActionsMap>(
    type: T,
    ...args: Parametrize<TActionsMap[T]>
  ) => void;
};

export type States<TPayloads extends SMap<any>> = {
  [Key in keyof TPayloads]: TPayloads[Key] extends null
    ? State<Key>
    : StateWithPayload<Key, TPayloads[Key]>;
}[keyof TPayloads];

export type Actions<TPayloads extends SMap<any>> = {
  [Key in keyof TPayloads]: IsNever<TPayloads[Key]> extends true
    ? Action<Key>
    : IsNull<TPayloads[Key]> extends true
    ? Action<Key>
    : ActionWithPayload<Key, TPayloads[Key]>;
}[keyof TPayloads];

export type Option<T = string> = { value: T; label: string };

export const lazyToOption =
  <T extends string | number>() =>
  (value: T, label: string): Option<T> => ({ value, label });

export const toOption = <T extends string | number>(value: T, label: string) =>
  lazyToOption<T>()(value, label);

// export const toOption2 =
//   <T, TKey4Value extends keyof T, TKeyForLabel extends keyof T>(
//     value: TKey4Value,
//     label: TKeyForLabel,
//   ) =>
//   (o: T) =>
//     toOption<TKey4Value>(o[value], o[label]);

export const optionFrom =
  <
    T,
    KValue extends keyof T,
    KLabel extends keyof T,
    V extends string | number = Extract<T[KValue], string | number>,
    L extends string = Extract<T[KLabel], string>,
  >(
    valueKey: KValue,
    labelKey: KLabel,
  ) =>
  (o: T) =>
    toOption<V>(o[valueKey] as V, o[labelKey] as L);

export const toOption3 =
  <T, KValue extends keyof T, KLabel extends keyof T>(
    valueKey: KValue,
    labelKey: KLabel,
  ) =>
  (o: T) =>
    toOption(
      o[valueKey] as Extract<T[KValue], string | number>,
      String(o[labelKey]),
    );

export const toOptionsConstructor = <T extends string | number>() =>
  toOption<T>;

type _ValueOf<TName extends string, T> = T extends TypedValue<TName, infer P>
  ? P
  : never;

export type ValueMap<T extends TypedValue<string, any>> = {
  [K in T['type']]: _ValueOf<K, T>;
};

export const toTypedValueConstructor =
  <T extends TypedValue>() =>
  <T2 extends T['type']>(type: T2, value: ValueMap<T>[T2]) =>
    ({ type, value } as any as T);

export type ValueOf<
  T extends TypedValue,
  T2 extends T['type'],
> = ValueMap<T>[T2];
export type TypeOf<T> = T extends { type: infer R }
  ? R extends string
    ? R
    : never
  : never;
type IsNever<T> = [T] extends [never] ? true : false;
type IsNull<T> = [T] extends [null] ? true : false;
type CondParams<TPayloads, T extends keyof TPayloads> = IsNever<
  TPayloads[T]
> extends true
  ? []
  : IsNull<TPayloads[T]> extends true
  ? []
  : [TPayloads[T]];

export type FSpread<TPayloads, TRes = null> = <T extends keyof TPayloads>(
  t: T,
  ...params: CondParams<TPayloads, T>
) => TRes;

export type RemoveNull<T> = T extends null
  ? never
  : T extends (infer U)[]
  ? RemoveNull<U>[]
  : T extends object
  ? { [K in keyof T]: RemoveNull<T[K]> }
  : T;
export type RemoveUndefined<T> = T extends undefined
  ? never
  : T extends (infer U)[]
  ? RemoveUndefined<U>[]
  : T extends object
  ? { [K in keyof T]: RemoveUndefined<T[K]> }
  : T;

export type EnsuredSubtype<T, TKeys extends keyof T> = RemoveNull<
  Required<Pick<T, TKeys>>
>;
export type SkipNull<T> = T extends null | undefined ? never : T;

export type Mapper<TIn, TOut> = (input: TIn) => TOut;

export type Prefixed<
  T extends string,
  O extends Record<string, any>,
> = T extends ''
  ? O
  : {
      [K in keyof O as `${T}${Extract<K, string>}`]: O[K];
    };
export type PickUnion<T, T2 extends T> = Extract<T, T2>;
export type ZodShape<T> = { [K in keyof Required<T>]: ZodTypeAny };
export type PickTypedValue<
  T extends TypedValue<any, any>,
  K extends T['type'],
> = T extends TypedValue<K, infer V> ? V : never;

export type PickTypedValue2<
  T extends TypedValue<any, any>,
  K extends T['type'],
> = T extends TypedValue<K, infer V> ? TypedValue<K, V> : never;

export type SkipNever<T> = {
  [K in keyof T as T[K] extends never ? never : K]: T[K];
};

export type NonNeverKeys<T> = {
  [K in keyof T]: T[K] extends never ? never : K;
}[keyof T];
